import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { mapBedbankBoardCode } from 'api/mappers/bedbankRateMap'
import { getOfferInclusionDescriptionFromMarkdown } from 'api/mappers/offerInclusionMap'
import { EmptyArray } from 'lib/array/arrayUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

type ServerUnifiedRate = definitions['UnifiedRate']
type ServerMetaKey = ServerUnifiedRate['meta']
type ServerLPCMetaKey = definitions['LPCMetaKey']
type ServerLPPMetaKey = definitions['LPPMetaKey']
type ServerRateType = ServerMetaKey['type']
type ServerInclusion = definitions['Inclusion']
type ServerRefundableStatus = definitions['RefundableStatus']

function isLpcMeta(meta: ServerMetaKey): meta is ServerLPCMetaKey {
  return meta.type === 'reservation'
}

function isBedbankMeta(meta: ServerMetaKey): meta is ServerLPPMetaKey {
  return meta.type === 'bedbank'
}

function mapInclusionDescription(
  serverRateType: ServerRateType,
  { text }: ServerInclusion,
): string {
  switch (serverRateType) {
    case 'reservation':
      return getOfferInclusionDescriptionFromMarkdown(text)
    case 'bedbank':
      return text
    default:
      assertUnreachable(serverRateType)
  }
}

function mapInclusion(
  serverRateType: ServerRateType,
  serverInclusion: ServerInclusion,
  inclusionType: 'normal' | 'bonus' | 'luxPlus',
): App.OfferInclusion {
  const description = mapInclusionDescription(serverRateType, serverInclusion)
  const symbol = determineOfferFeatureSymbol(serverInclusion.icon || description)
  const isBonus = inclusionType === 'bonus'
  const isLuxPlusBonus = inclusionType === 'luxPlus'
  return {
    id: uuidv4(),
    symbol,
    description,
    isBonus,
    isLuxPlusBonus,
  }
}

function mapHeadlineCancellationPolicy(
  serverRefundableStatus: ServerRefundableStatus,
  serverFullyRefundableUntil: string | undefined,
  serverPartiallyRefundableUntil: string | undefined,
): ['fullyRefundable' | 'partiallyRefundable', string] | ['nonRefundable', undefined] {
  switch (serverRefundableStatus) {
    case 'refundable':
      return ['fullyRefundable', serverFullyRefundableUntil!]
    case 'partially':
      return ['partiallyRefundable', serverPartiallyRefundableUntil!]
    case 'non-refundable':
      return ['nonRefundable', undefined]
    default:
      return assertUnreachable(serverRefundableStatus)
  }
}

export default function propertyRateMap(serverRate: ServerUnifiedRate): App.PropertyRate {
  const serverRateMeta = serverRate.meta
  // NB: These fields were last audited against v0.5.4 of `contract-svc-accommodation`.
  const fixedTotal = {
    ...serverRate.total,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: serverRate.total.displayPrice - serverRate.total.propertyFees,
  }
  const fixedLuxPlus = serverRate.luxPlus && {
    ...serverRate.luxPlus,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: serverRate.luxPlus.displayPrice - serverRate.luxPlus.propertyFees,
  }
  const [headlineCancellationPolicyType, headlineCancellationPolicyStart] = mapHeadlineCancellationPolicy(serverRate.refundableStatus, serverRate.fullRefundableUntil, serverRate.partiallyRefundableUntil)
  const baseRate: App.BasePropertyRate = {
    /* Identifiers */
    propertyId: serverRate.propertyId,
    roomId: serverRate.roomId,
    rateId: serverRate.id,
    /* Content */
    // `serverRate.name` is deprecated.
    /* Deduplication keys */
    groupKey: serverRate.groupKey,
    bundlingKey: serverRate.bundlingKey,
    isFlightBundle: !!serverRateMeta.isFlightBundle,
    /* Inclusions */
    /* Cancellation policies */
    /* Pricing */
    /* (Irrelevant) */
    // `serverRate.occupancies` is not set.
    // `serverRate.margin` is filtered out for non-admins.
    // `serverRate.groupScore` is filtered out for non-admins.
    /* TODO: Audit, document, tighten contract */
    total: fixedTotal,
    luxPlusTotal: fixedLuxPlus,
    fullyRefundableUntil: serverRate.fullRefundableUntil,
    cancellationPolicies: serverRate.cancellationPolicies.length ? serverRate.cancellationPolicies : EmptyArray,
    headlineCancellationPolicyType,
    headlineCancellationPolicyStart,
    boardCode: serverRate.boardCode,
    hasNonMealInclusions: !!serverRate.hasNonMealInclusions,
    inclusions: serverRate.inclusions.length ? serverRate.inclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')) : EmptyArray,
    bonusInclusions: serverRate.bonusInclusions.length ? serverRate.bonusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'bonus')) : EmptyArray,
    luxPlusInclusions: serverRate.luxPlusInclusions.length ? serverRate.luxPlusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'luxPlus')) : EmptyArray,
    includesFlights: false,
    includedGuestsExceeded: false,
    includedGuestsExceededPolicy: undefined,
  }
  if (isLpcMeta(serverRateMeta)) {
    // TODO: Remove hack once accommodation service returns this value for us.
    fixedTotal.discount = getValueOffPercent(fixedTotal.valuedUpToPrice, fixedTotal.displayPrice)
    if (fixedLuxPlus) {
      fixedLuxPlus.discount = getValueOffPercent(fixedLuxPlus.valuedUpToPrice, fixedLuxPlus.displayPrice)
    }
    return {
      ...baseRate,
      /* Identifiers */
      type: 'lpc',
      lpcPropertyId: serverRateMeta.reservationPropertyId,
      lpcRoomId: serverRateMeta.reservationRoomTypeId,
      lpcRateId: serverRateMeta.reservationRoomRateId,
      /* Content */
      name: serverRateMeta.name,
      /* TODO: Audit, document, tighten contract */
      offerId: serverRateMeta.offerId,
      packageId: serverRateMeta.packageId,
      lePackageId: serverRateMeta.lePackageId,
      includedGuestsExceeded: !!serverRateMeta.exceedsIncludedGuests,
    }
  } else if (isBedbankMeta(serverRateMeta)) {
    // TODO: Remove hack once accommodation service returns this value for us.
    fixedTotal.discount = Math.floor(fixedTotal.discount * 100)

    if (fixedLuxPlus) {
      fixedLuxPlus.discount = Math.floor(fixedLuxPlus.discount * 100)
    }

    return {
      ...baseRate,
      /* Identifiers */
      type: 'bedbank',
      // `serverRateMeta.supplier` is filtered out for non-admins.
      bedbankSupplierCode: serverRateMeta.supplierCode!, // This _should_ be always set.
      bedbankPropertyId: serverRateMeta.bedbankPropertyId,
      bedbankRoomId: serverRateMeta.bedbankRoomId,
      bedbankRateId: serverRateMeta.bedbankRateId,
      /* TODO: Audit, document, tighten contract */
      rateGroupId: serverRateMeta.rateGroupId,
      bedGroups: serverRateMeta.bedGroups?.length ? serverRateMeta.bedGroups : EmptyArray, // TODO: Tighten contract.
      canPayLater: !!serverRateMeta.paymentSchedule,
      corporate: !!serverRateMeta.corporate,
      bedbankBoardCode: mapBedbankBoardCode(serverRate.boardCode),
    }
  }
  assertUnreachable(serverRateMeta)
}
